import {
  AlertCircle,
  Check,
  ChevronsUpDown,
  ChevronUp,
  Loader2,
  MoreHorizontal,
  Paperclip,
  RotateCcw,
  Send,
  X,
} from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  FrontMessageAttachment,
  FrontMessageDetail,
  useGetFrontConversationByDbIdQuery,
  useSendFrontReplyMutation,
} from '@/services/api/frontMessageService';
import { UnifiedCommunication } from '@/services/types/client-communications-types';

import { useClientCommunications } from '../ClientCommunicationsContext';

interface EmailDetailPaneProps {
  communication: UnifiedCommunication;
}

// Optimistic message for immediate UI feedback
interface OptimisticMessage {
  id: string;
  body: string;
  subject: string;
  attachments: { filename: string }[];
  status: 'sending' | 'sent' | 'error';
  error?: string;
  created_at: string;
}

// Helper to download file with auth
const downloadWithAuth = async (url: string, filename: string) => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) throw new Error('Download failed');

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Download error:', error);
  }
};

// Format file size
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Format date for display
const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Format date/time for expanded message
const formatDateTime = (dateStr: string) => {
  const date = new Date(dateStr);
  return `${date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })} ${date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })}`;
};

// Attachment chip for messages
const AttachmentChip: React.FC<{
  attachment: FrontMessageAttachment;
  matterId: string;
  messageId: string;
}> = ({ attachment, matterId, messageId }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const downloadUrl = `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/matters/${matterId}/front/messages/${messageId}/attachments/${attachment.id}/download`;

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await downloadWithAuth(downloadUrl, attachment.filename);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={isDownloading}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-zinc-100 hover:bg-zinc-200 transition-colors text-xs font-medium border border-zinc-200"
    >
      {isDownloading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-600" />
      ) : (
        <Paperclip className="h-3.5 w-3.5 text-zinc-600" />
      )}
      <span className="text-zinc-800 truncate max-w-[160px]">
        {attachment.filename}
      </span>
    </button>
  );
};

// Single email message in thread
const EmailMessage: React.FC<{
  message: FrontMessageDetail;
  matterId: string;
  isLast: boolean;
  isFirst: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
}> = ({ message, matterId, isLast, isFirst, isExpanded, onToggleExpand }) => {
  const [showQuoted, setShowQuoted] = useState(false);

  const getSenderName = () => {
    if (message.author) {
      const fullName =
        `${message.author.first_name || ''} ${message.author.last_name || ''}`.trim();
      return fullName || message.author.email;
    }
    // Fallback to "Client" for inbound if no author info
    return message.is_inbound ? 'Client' : 'You';
  };

  const getSenderEmail = () => {
    return message.author?.email || '';
  };

  const getRecipientEmails = () => {
    if (message.recipients && message.recipients.length > 0) {
      const emails = message.recipients
        .map((r) => r.handle || r.name)
        .filter(Boolean);
      if (emails.length > 2) {
        return `${emails.slice(0, 2).join(', ')} +${emails.length - 2} more`;
      }
      return emails.join(', ');
    }
    return '';
  };

  const senderName = getSenderName();
  const senderEmail = getSenderEmail();
  const recipientEmails = getRecipientEmails();
  const isClient = message.is_inbound;

  // Clean preview text by removing quoted content patterns
  const getCleanPreview = () => {
    const rawText = message.blurb || message.text || '';
    // Remove everything starting from "On [date] wrote:" pattern
    const quotePattern =
      /\s*On\s+\w{3},\s+\w{3}\s+\d{1,2},\s+\d{4}\s+at\s+\d{1,2}:\d{2}\s*(AM|PM).*$/i;
    const cleaned = rawText.replace(quotePattern, '').trim();
    // Also handle simpler "On ... wrote:" patterns
    const simplePattern = /\s*On\s+.{0,60}\s+wrote:.*$/i;
    const finalCleaned = cleaned.replace(simplePattern, '').trim();
    return finalCleaned || 'No content';
  };

  // Process body to handle quoted text
  const processedBody = useMemo(() => {
    if (!message.body) return { html: '', hasQuotes: false };

    const parser = new DOMParser();
    const doc = parser.parseFromString(message.body, 'text/html');

    // Handle case where doc.body is null (malformed HTML or parsing failure)
    if (!doc.body) {
      return { html: message.body, hasQuotes: false };
    }

    // Find standard quoted elements
    const quotedElements = doc.querySelectorAll(
      'blockquote, .gmail_quote, [class*="quote"], .gmail_extra, .yahoo_quoted, .moz-cite-prefix',
    );
    let hasQuotes = quotedElements.length > 0;

    // Also detect "On [date], [person] wrote:" patterns which indicate quoted replies
    const quotePatterns = [
      /On .{1,50}wrote:/i,
      /On .{1,50}at .{1,20}(AM|PM).{1,50}wrote:/i,
      /^-{2,}.*Original Message.*-{2,}/im,
      /^From:.*\nSent:.*\nTo:/im,
    ];

    if (!showQuoted) {
      // Remove standard quoted elements
      quotedElements.forEach((el) => el.remove());

      // Find and remove text-based quote patterns
      const walkAndRemove = (node: Node): boolean => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent || '';
          const matchedPattern = quotePatterns.some((pattern) =>
            pattern.test(text),
          );
          if (matchedPattern) {
            hasQuotes = true;
            // Remove this node and all following siblings
            let current: Node | null = node;
            while (current) {
              const next: Node | null = current.nextSibling;
              current.parentNode?.removeChild(current);
              current = next;
            }
            return true;
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as Element;
          const text = el.textContent || '';
          const matchedPattern = quotePatterns.some((pattern) =>
            pattern.test(text.substring(0, 200)),
          );
          if (matchedPattern) {
            hasQuotes = true;
            // Don't remove the body element itself, only clear its contents
            if (el === doc.body) {
              el.innerHTML = '';
            } else {
              el.remove();
            }
            return true;
          }
          // Check children
          const children = Array.from(node.childNodes);
          const foundInChild = children.some((child) => walkAndRemove(child));
          if (foundInChild) return true;
        }
        return false;
      };

      walkAndRemove(doc.body);
    }

    return { html: doc.body.innerHTML, hasQuotes };
  }, [message.body, showQuoted]);

  const hasAttachments = message.attachments && message.attachments.length > 0;

  // Collapsed view (show for all messages except expanded)
  if (!isExpanded) {
    return (
      <div
        className={cn(
          'border-b border-zinc-100 last:border-b-0 py-3 px-1 cursor-pointer hover:bg-zinc-50 transition-colors',
          isClient && 'bg-blue-50/20 hover:bg-blue-50/30',
        )}
        onClick={onToggleExpand}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onToggleExpand();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span
              className={cn(
                'text-sm font-bold',
                isClient ? 'text-blue-800' : 'text-zinc-900',
              )}
            >
              {senderName}
            </span>
            {isClient && (
              <span className="text-[10px] font-semibold text-blue-600 bg-blue-100/60 px-1.5 py-0.5 rounded">
                Client
              </span>
            )}
          </div>
          <span className="text-xs font-semibold text-zinc-500 flex-shrink-0">
            {message.created_at ? formatDate(message.created_at) : ''}
          </span>
        </div>
        <p className="text-sm font-medium text-zinc-600 truncate mt-0.5">
          {getCleanPreview()}
        </p>
      </div>
    );
  }

  // Expanded view (show full message)
  return (
    <div
      className={cn(
        'border-b border-zinc-100 last:border-b-0 py-4 px-1',
        isClient && 'bg-blue-50/20',
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'text-sm font-bold',
                isClient ? 'text-blue-800' : 'text-zinc-900',
              )}
            >
              {senderName}
            </span>
            {senderEmail && (
              <span className="text-xs text-zinc-500">
                &lt;{senderEmail}&gt;
              </span>
            )}
            {isClient && (
              <span className="text-[10px] font-semibold text-blue-600 bg-blue-100/60 px-1.5 py-0.5 rounded">
                Client
              </span>
            )}
          </div>
          {recipientEmails && (
            <button
              type="button"
              onClick={onToggleExpand}
              className="text-xs font-medium text-zinc-500 hover:text-zinc-700 flex items-center gap-1 mt-0.5"
            >
              to {recipientEmails}
              <ChevronUp className="h-3 w-3" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-zinc-500">
            {message.created_at ? formatDateTime(message.created_at) : ''}
          </span>
          <button
            type="button"
            className="p-1 hover:bg-zinc-100 rounded transition-colors"
          >
            <MoreHorizontal className="h-4 w-4 text-zinc-400" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="text-sm leading-relaxed text-zinc-900 mb-3">
        {processedBody.html && (
          <div
            className="prose prose-sm max-w-none prose-p:my-2 prose-p:leading-relaxed prose-p:font-semibold prose-p:text-zinc-900 [&_*]:text-zinc-900 [&_a]:text-primary [&_a]:no-underline hover:[&_a]:underline"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: processedBody.html }}
          />
        )}
        {!processedBody.html && message.text && (
          <div className="whitespace-pre-wrap font-semibold text-zinc-900">
            {message.text}
          </div>
        )}
        {!processedBody.html && !message.text && (
          <div className="italic text-zinc-500">No content</div>
        )}

        {/* Quoted text toggle */}
        {processedBody.hasQuotes && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowQuoted(!showQuoted);
            }}
            className="mt-2 text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            {showQuoted ? 'Hide quoted text' : 'Show quoted text'}
          </button>
        )}
      </div>

      {/* Attachments */}
      {hasAttachments && (
        <div className="flex flex-wrap gap-2">
          {message.attachments.map((att) => (
            <AttachmentChip
              key={att.id}
              attachment={att}
              matterId={matterId}
              messageId={message.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Optimistic message component (shown while sending)
const OptimisticEmailMessage: React.FC<{
  message: OptimisticMessage;
  onRetry?: () => void;
}> = ({ message, onRetry }) => {
  const isSending = message.status === 'sending';
  const hasError = message.status === 'error';
  const isSent = message.status === 'sent';

  return (
    <div
      className={cn(
        'py-4 px-1 border-b border-zinc-200/60 last:border-b-0',
        hasError && 'bg-red-50/50',
        isSending && 'opacity-80',
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-zinc-900">You</span>
          {isSending && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-600">
              <Loader2 className="h-3 w-3 animate-spin" />
              Sending...
            </span>
          )}
          {isSent && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600">
              <Check className="h-3 w-3" />
              Sent
            </span>
          )}
          {hasError && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600">
              <AlertCircle className="h-3 w-3" />
              Failed to send
            </span>
          )}
        </div>

        {/* Retry button for errors */}
        {hasError && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="p-1 hover:bg-zinc-100 rounded transition-colors"
            title="Retry"
          >
            <RotateCcw className="h-4 w-4 text-zinc-600" />
          </button>
        )}
      </div>

      {/* Body */}
      <div className="text-sm leading-relaxed text-zinc-800 font-medium">
        <div className="whitespace-pre-wrap">{message.body}</div>

        {/* Attachments */}
        {message.attachments.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {message.attachments.map((att, idx) => (
              <span
                key={`${att.filename}-${idx}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-zinc-100 text-xs font-medium border border-zinc-200"
              >
                <Paperclip className="h-3.5 w-3.5 text-zinc-600" />
                <span className="text-zinc-800 truncate max-w-[160px]">
                  {att.filename}
                </span>
              </span>
            ))}
          </div>
        )}

        {/* Error message */}
        {hasError && message.error && (
          <p className="mt-2 text-xs font-semibold text-red-600">
            {message.error}
          </p>
        )}
      </div>
    </div>
  );
};

// Reply attachment chip
const ReplyAttachment: React.FC<{
  file: File;
  onRemove: () => void;
}> = ({ file, onRemove }) => (
  <div className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 bg-zinc-100 rounded text-xs font-medium">
    <Paperclip className="h-3 w-3 text-zinc-600" />
    <span className="text-zinc-700 truncate max-w-[100px]">{file.name}</span>
    <span className="text-zinc-600 text-[10px] font-semibold">
      {formatFileSize(file.size)}
    </span>
    <button
      type="button"
      onClick={onRemove}
      className="p-0.5 hover:bg-zinc-200 rounded transition-colors"
    >
      <X className="h-3 w-3 text-zinc-600" />
    </button>
  </div>
);

export const EmailDetailPane: React.FC<EmailDetailPaneProps> = ({
  communication,
}) => {
  const { matterId, refreshCommunications } = useClientCommunications();

  // Reply composer state
  const [showReplyComposer, setShowReplyComposer] = useState(false);
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [replyAttachments, setReplyAttachments] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Track which messages are expanded (by message ID)
  const [expandedMessageIds, setExpandedMessageIds] = useState<Set<string>>(
    new Set(),
  );

  // Optimistic messages state
  const [optimisticMessages, setOptimisticMessages] = useState<
    OptimisticMessage[]
  >([]);
  const pendingRetryRef = useRef<{
    body: string;
    subject: string;
    attachments: { filename: string; base64: string }[];
  } | null>(null);

  // Fetch conversation data
  const { data, isLoading, error } = useGetFrontConversationByDbIdQuery(
    { matterId, conversationDbId: communication.id },
    { skip: !communication.id },
  );

  const [sendReply] = useSendFrontReplyMutation();

  // Initialize subject when conversation loads
  const conversationSubject = data?.conversation?.subject;
  const initializeSubject = useCallback(() => {
    if (conversationSubject && !replySubject) {
      setReplySubject(
        conversationSubject.startsWith('Re:')
          ? conversationSubject
          : `Re: ${conversationSubject}`,
      );
    }
  }, [conversationSubject, replySubject]);

  React.useEffect(() => {
    initializeSubject();
  }, [initializeSubject]);

  // Initialize expanded state - expand the last message by default
  const messages = data?.messages ?? [];
  const lastMessageId =
    messages.length > 0 ? messages[messages.length - 1]?.id : undefined;
  const hasInitializedExpanded = useRef(false);

  useEffect(() => {
    if (lastMessageId && !hasInitializedExpanded.current) {
      hasInitializedExpanded.current = true;
      setExpandedMessageIds(new Set([lastMessageId]));
    }
  }, [lastMessageId]);

  // Toggle individual message expansion
  const toggleMessageExpanded = useCallback((messageId: string) => {
    setExpandedMessageIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  }, []);

  // Expand all messages
  const handleExpandAll = useCallback(() => {
    const allIds = new Set(messages.map((m) => m.id));
    setExpandedMessageIds(allIds);
  }, [messages]);

  // Collapse all (keep only the last message expanded)
  const handleCollapseAll = useCallback(() => {
    if (lastMessageId !== undefined) {
      setExpandedMessageIds(new Set([lastMessageId]));
    } else {
      setExpandedMessageIds(new Set());
    }
  }, [lastMessageId]);

  // Check if all messages are expanded
  const allExpanded =
    messages.length > 0 && messages.every((m) => expandedMessageIds.has(m.id));

  const handleAddAttachment = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      setReplyAttachments((prev) => [...prev, ...files]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [],
  );

  const handleRemoveAttachment = useCallback((index: number) => {
    setReplyAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSendReply = useCallback(
    async (retryData?: {
      body: string;
      subject: string;
      attachments: { filename: string; base64: string }[];
      optimisticId: string;
    }) => {
      const bodyToSend = retryData?.body || replyBody;
      const subjectToSend = retryData?.subject || replySubject;

      if (!data?.conversation || !bodyToSend.trim()) return;

      // Generate optimistic message ID
      const optimisticId =
        retryData?.optimisticId || `optimistic-${Date.now()}`;

      // Convert files to base64 (skip if retrying with existing data)
      let attachmentsBase64: { filename: string; base64: string }[];
      if (retryData?.attachments) {
        attachmentsBase64 = retryData.attachments;
      } else {
        attachmentsBase64 = await Promise.all(
          replyAttachments.map(
            (file) =>
              new Promise<{ filename: string; base64: string }>((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  const base64 = (reader.result as string).split(',')[1];
                  resolve({ filename: file.name, base64 });
                };
                reader.readAsDataURL(file);
              }),
          ),
        );
      }

      // Add optimistic message immediately (or update existing one for retry)
      const optimisticMessage: OptimisticMessage = {
        id: optimisticId,
        body: bodyToSend,
        subject: subjectToSend,
        attachments: attachmentsBase64.map((a) => ({ filename: a.filename })),
        status: 'sending',
        created_at: new Date().toISOString(),
      };

      setOptimisticMessages((prev) => {
        const existing = prev.find((m) => m.id === optimisticId);
        if (existing) {
          return prev.map((m) =>
            m.id === optimisticId
              ? { ...m, status: 'sending', error: undefined }
              : m,
          );
        }
        return [...prev, optimisticMessage];
      });

      // Clear form immediately for better UX
      if (!retryData) {
        setReplyBody('');
        setReplyAttachments([]);
        setShowReplyComposer(false);
      }

      setIsSending(true);

      try {
        await sendReply({
          matterId,
          conversationId: data.conversation.id,
          conversationDbId: communication.id, // DB UUID for cache invalidation
          subject: subjectToSend,
          body: bodyToSend,
          attachments: attachmentsBase64,
        }).unwrap();

        // Mark as sent
        setOptimisticMessages((prev) =>
          prev.map((m) =>
            m.id === optimisticId ? { ...m, status: 'sent' } : m,
          ),
        );

        // Clear retry data
        pendingRetryRef.current = null;

        // Refresh communications list
        refreshCommunications();
      } catch (sendError) {
        // Mark as error
        const errorMessage =
          sendError instanceof Error
            ? sendError.message
            : 'Failed to send message';
        setOptimisticMessages((prev) =>
          prev.map((m) =>
            m.id === optimisticId
              ? { ...m, status: 'error', error: errorMessage }
              : m,
          ),
        );

        // Store retry data
        pendingRetryRef.current = {
          body: bodyToSend,
          subject: subjectToSend,
          attachments: attachmentsBase64,
        };
      } finally {
        setIsSending(false);
      }
    },
    [
      communication.id,
      data?.conversation,
      matterId,
      replyBody,
      replySubject,
      replyAttachments,
      sendReply,
      refreshCommunications,
    ],
  );

  const handleRetry = useCallback(
    (optimisticId: string) => {
      const messageToRetry = optimisticMessages.find(
        (m) => m.id === optimisticId,
      );
      if (!messageToRetry || !pendingRetryRef.current) return;

      handleSendReply({
        body: messageToRetry.body,
        subject: messageToRetry.subject,
        attachments: pendingRetryRef.current.attachments,
        optimisticId,
      });
    },
    [optimisticMessages, handleSendReply],
  );

  // Track previous message count to detect new messages
  const prevMessageCountRef = useRef(data?.messages?.length ?? 0);

  // Remove "sent" optimistic messages when real messages appear
  React.useEffect(() => {
    const currentCount = data?.messages?.length ?? 0;
    const prevCount = prevMessageCountRef.current;

    if (currentCount > prevCount && optimisticMessages.length > 0) {
      const sentMessages = optimisticMessages.filter(
        (m) => m.status === 'sent',
      );
      if (sentMessages.length > 0) {
        setOptimisticMessages((prev) =>
          prev.filter((m) => m.status !== 'sent'),
        );
      }
    }

    prevMessageCountRef.current = currentCount;
  }, [data?.messages?.length, optimisticMessages]);

  // Scroll to bottom helper
  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, []);

  // Auto-scroll to bottom when conversation loads
  useEffect(() => {
    if (!isLoading && data?.messages && data.messages.length > 0) {
      // Delay to ensure DOM is fully rendered
      const timer = setTimeout(scrollToBottom, 200);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isLoading, data?.messages, scrollToBottom]);

  // Auto-scroll when reply composer opens
  useEffect(() => {
    if (showReplyComposer) {
      // Small delay to let the composer render
      setTimeout(scrollToBottom, 100);
    }
  }, [showReplyComposer, scrollToBottom]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-sm font-semibold text-zinc-600">
          Failed to load thread
        </p>
      </div>
    );
  }

  const { conversation } = data;

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header with subject and actions */}
      <div className="px-6 py-2.5 border-b border-zinc-200">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <h2 className="text-base font-bold text-zinc-900 leading-tight truncate">
                {conversation.subject || '(No subject)'}
              </h2>
              {messages.length > 1 && (
                <span className="text-xs font-semibold text-zinc-500 shrink-0">
                  ({messages.length})
                </span>
              )}
            </div>
          </div>
          {messages.length > 1 && (
            <button
              type="button"
              onClick={allExpanded ? handleCollapseAll : handleExpandAll}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 rounded transition-all duration-150"
              title={allExpanded ? 'Collapse all' : 'Expand all'}
            >
              <ChevronsUpDown
                className={`h-3.5 w-3.5 transition-transform duration-150 ${allExpanded ? 'rotate-180' : ''}`}
              />
              <span>{allExpanded ? 'Collapse' : 'Expand'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Messages thread */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-6 py-2"
      >
        {messages.map((msg, index) => (
          <EmailMessage
            key={msg.id}
            message={msg}
            matterId={matterId}
            isFirst={index === 0}
            isLast={
              index === messages.length - 1 && optimisticMessages.length === 0
            }
            isExpanded={expandedMessageIds.has(msg.id)}
            onToggleExpand={() => toggleMessageExpanded(msg.id)}
          />
        ))}
        {/* Optimistic messages (being sent) */}
        {optimisticMessages.map((msg) => (
          <OptimisticEmailMessage
            key={msg.id}
            message={msg}
            onRetry={() => handleRetry(msg.id)}
          />
        ))}
      </div>

      {/* Reply section */}
      <div className="border-t border-zinc-200 bg-white">
        {!showReplyComposer ? (
          /* Reply button */
          <div className="px-6 py-4">
            <Button
              onClick={() => setShowReplyComposer(true)}
              className="bg-primary hover:bg-primary/90 text-white font-semibold rounded-full px-6"
            >
              Reply
            </Button>
          </div>
        ) : (
          /* Reply composer */
          <div className="p-4">
            {/* Subject */}
            <div className="px-2 py-2 border-b border-zinc-200">
              <input
                type="text"
                value={replySubject}
                onChange={(e) => setReplySubject(e.target.value)}
                placeholder="Subject"
                className="w-full text-sm font-semibold text-zinc-800 placeholder:text-zinc-500 bg-transparent outline-none"
              />
            </div>

            {/* Body */}
            <div className="px-2 py-3">
              <textarea
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                placeholder="Write your reply..."
                rows={4}
                className="w-full text-sm font-medium text-zinc-800 placeholder:text-zinc-500 bg-transparent outline-none resize-none leading-relaxed"
                // eslint-disable-next-line jsx-a11y/no-autofocus -- Intentional UX: focus reply textarea when composer opens
                autoFocus
              />
            </div>

            {/* Attachments */}
            {replyAttachments.length > 0 && (
              <div className="px-2 pb-2 flex flex-wrap gap-1.5">
                {replyAttachments.map((file, index) => (
                  <ReplyAttachment
                    key={`${file.name}-${index}`}
                    file={file}
                    onRemove={() => handleRemoveAttachment(index)}
                  />
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="px-2 py-2 flex items-center justify-between">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileChange}
              />
              <button
                type="button"
                onClick={handleAddAttachment}
                className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
              >
                <Paperclip className="h-4 w-4 text-zinc-600" />
              </button>

              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="font-semibold"
                  onClick={() => {
                    setShowReplyComposer(false);
                    setReplyBody('');
                    setReplyAttachments([]);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="gap-1.5 rounded-full px-4 font-semibold"
                  disabled={!replyBody.trim() || isSending}
                  onClick={() => handleSendReply()}
                >
                  {isSending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  Send
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailDetailPane;
